// src/Components/ImportMarkers/ArchivedBatches.js

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import {
  MenuAddNewGrey,
  MenuSaveGrey,
  MenuDeleteGrey,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';

import { MenuImportGrey, MenuUploadGrey } from '../../assets/icons/CustomIconsAdd2';

import { MenuUnArchive, MenuUnArchiveGrey } from '../../assets/icons/CustomIconsAdd4';

import ExcelHistoryView from './ExcelHistoryView';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import { collection, onSnapshot, writeBatch, doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

const ICON_SIZE = 64;

const PAGE1_UPLOAD_SCALE = 4.9;
const PAGE1_UPLOAD_ICON_X = 25;
const PAGE1_UPLOAD_ICON_Y = 38;
const PAGE1_UPLOAD_TEXT_SIZE = '18px';
const PAGE1_UPLOAD_TEXT_Y = 108;

const PAGE2_IMPORT_SCALE = 7.5;
const PAGE2_IMPORT_ICON_X = 25;
const PAGE2_IMPORT_ICON_Y = 37;
const PAGE2_IMPORT_TEXT_SIZE = '18px';
const PAGE2_IMPORT_TEXT_Y = 108;

export default function ArchivedBatches() {
  const [archivedBatches, setArchivedBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [showReinstateConfirm, setShowReinstateConfirm] = useState(false);

  useEffect(() => {
    // Listener to load specifically the excelArchives collection
    const q = collection(db, 'excelArchives');
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => new Date(b.importDate) - new Date(a.importDate));
        setArchivedBatches(data);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching archived batches:', error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const executeReinstate = async () => {
    setShowReinstateConfirm(false);
    if (selectedIds.length === 0) return;
    setIsProcessing(true);

    try {
      const batch = writeBatch(db);

      for (const batchId of selectedIds) {
        const archiveRef = doc(db, 'excelArchives', batchId);
        const archiveSnap = await getDoc(archiveRef);

        if (archiveSnap.exists()) {
          const data = archiveSnap.data();
          const importRef = doc(db, 'excelImports', batchId);

          batch.set(importRef, data);
          batch.delete(archiveRef);
        }
      }

      await batch.commit();

      setAlertState({
        open: true,
        message: `Successfully reinstated ${selectedIds.length} batch(es) to Active Imports.`,
        severity: 'success',
      });
      setSelectedIds([]);
    } catch (error) {
      console.error('Error reinstating batch:', error);
      setAlertState({
        open: true,
        message: `Failed to reinstate batches: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState((prev) => ({ ...prev, open: false }));
  };

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {/* --- TOP TOOLBAR --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
              <MenuUploadGrey
                sx={{ fontSize: ICON_SIZE }}
                iconScale={PAGE1_UPLOAD_SCALE}
                iconX={PAGE1_UPLOAD_ICON_X}
                iconY={PAGE1_UPLOAD_ICON_Y}
                textSize={PAGE1_UPLOAD_TEXT_SIZE}
                textY={PAGE1_UPLOAD_TEXT_Y}
                label='Upload'
              />
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
              <MenuImportGrey
                sx={{ fontSize: ICON_SIZE }}
                iconScale={PAGE2_IMPORT_SCALE}
                iconX={PAGE2_IMPORT_ICON_X}
                iconY={PAGE2_IMPORT_ICON_Y}
                textSize={PAGE2_IMPORT_TEXT_SIZE}
                textY={PAGE2_IMPORT_TEXT_Y}
                label='Import'
              />
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          {/* Delete Button - Disabled as per instructions */}
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={{ ...toolBlockStyle, position: 'relative' }}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* --- SECONDARY HEADER FOR ARCHIVE VIEW --- */}
      <Box
        sx={{
          height: '55px',
          minHeight: '55px',
          maxHeight: '55px',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f9f9f9',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 49,
          px: 2,
          py: 0.5,
          boxSizing: 'border-box',
        }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            pr: 2,
          }}></Box>

        <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

        <Box
          sx={{
            width: '110px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ml: 1,
          }}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(5px)' }}
            disabled={selectedIds.length === 0 || isProcessing}
            onClick={() => setShowReinstateConfirm(true)}
            title='UnArchive Selected Batches'>
            {selectedIds.length > 0 && !isProcessing ? (
              <MenuUnArchive sx={{ fontSize: ICON_SIZE * 1.5 }} />
            ) : (
              <MenuUnArchiveGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* --- MAIN CONTENT AREA --- */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <ExcelHistoryView
          importHistory={archivedBatches}
          isLoadingHistory={isLoading}
          selectedHistoryIds={selectedIds}
          setSelectedHistoryIds={setSelectedIds}
        />
      </Box>

      <ConfirmDialog
        open={showReinstateConfirm}
        onClose={() => setShowReinstateConfirm(false)}
        onConfirm={executeReinstate}
        message='The selected markers will be moved to the Batch Import working list.'
        severity='info'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <Snackbar
        open={alertState.open}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 100005 }}>
        <Alert
          onClose={handleCloseAlert}
          severity={alertState.severity}
          sx={{ width: '100%' }}>
          {alertState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
